import axios, { AxiosResponse } from 'axios';
import { getEnv } from 'src/utils/env.vite';

import { INTERVAL_TIME_120000 } from 'src/utils/common/utils';
import { getEosToken } from './EosTokenService';

export const eosAxiosConfig = {
  baseURL: getEnv('VITE_APP_EOS_API_BASE_URL') ?? '',
  timeout: INTERVAL_TIME_120000,
  headers: {},
};

export const EosApi = axios.create(eosAxiosConfig);

EosApi.interceptors.request.use((config) => {
  const towerPCUserName = sessionStorage.getItem('tower-pc-user');
  const token = sessionStorage.getItem('access_token') ?? '';
  const eosToken = getEosToken();

  const newConfig = { ...config };

  if (newConfig.headers) {
    newConfig.headers.Authorization = `Bearer ${token}`;
    if (towerPCUserName) {
      newConfig.headers.TowerPcUserName = towerPCUserName;
    }

    if (eosToken) {
      newConfig.headers.traceparent = eosToken;
    }
  }

  return newConfig;
});

export const getEosRequestAbortionPieces = () => {
  const abort = axios.CancelToken.source();
  const connectionTimeout = setTimeout(() => {
    abort.cancel(`Connection timeout of ${eosAxiosConfig.timeout}ms.`);
  }, eosAxiosConfig.timeout);

  return { abort, connectionTimeout };
};

export const eosGet = async (
  path: string,
  config: { signal?: AbortSignal } = {},
): Promise<AxiosResponse> => {
  const { abort, connectionTimeout } = getEosRequestAbortionPieces();
  return EosApi.get(path, {
    cancelToken: abort.token,
    signal: config.signal,
    ...config,
  }).then((resp) => {
    clearTimeout(connectionTimeout);
    return resp;
  });
};

export const eosPost = async (
  path: string,
  body,
  config: { signal?: AbortSignal } = {},
): Promise<AxiosResponse> => {
  const { abort, connectionTimeout } = getEosRequestAbortionPieces();

  return EosApi.post(path, body, {
    cancelToken: abort.token,
    signal: config.signal,
    ...config,
  }).then((resp) => {
    clearTimeout(connectionTimeout);
    return resp;
  });
};

export const eosPatch = async (path: string, body): Promise<AxiosResponse> => {
  const { abort, connectionTimeout } = getEosRequestAbortionPieces();

  return EosApi.patch(path, body, {
    cancelToken: abort.token,
  }).then((resp) => {
    clearTimeout(connectionTimeout);
    return resp;
  });
};

export const eosPut = async (path: string, body): Promise<AxiosResponse> => {
  const { abort, connectionTimeout } = getEosRequestAbortionPieces();

  return EosApi.put(path, body, {
    cancelToken: abort.token,
  }).then((resp) => {
    clearTimeout(connectionTimeout);
    return resp;
  });
};

export const eosDelete = async (path: string, body): Promise<AxiosResponse> => {
  const { connectionTimeout } = getEosRequestAbortionPieces();
  return EosApi.delete(path, { data: body }).then((resp) => {
    clearTimeout(connectionTimeout);
    return resp;
  });
};
